import { useState } from 'react'
import { X, Plus, FileText, Layout, ShoppingBag, Receipt, Calendar, Megaphone, Rocket, Store, Home, UtensilsCrossed, Dumbbell, Briefcase, Search, Layers } from 'lucide-react'
import { PRELOADED_TEMPLATES } from '../utils/preloadedTemplates'

const CATEGORY_ICONS = {
  Onboarding: FileText,
  Content: Layout,
  Marketing: ShoppingBag,
  Transactional: Receipt,
  Events: Calendar,
  General: Megaphone,
  SaaS: Rocket,
  eCommerce: Store,
  'Real Estate': Home,
  Restaurant: UtensilsCrossed,
  Fitness: Dumbbell,
  Agency: Briefcase
}

const CATEGORY_COLORS = {
  Onboarding: '#6366f1',
  Content: '#0891b2',
  Marketing: '#dc2626',
  Transactional: '#059669',
  Events: '#7c3aed',
  General: '#f59e0b',
  SaaS: '#8b5cf6',
  eCommerce: '#ec4899',
  'Real Estate': '#14b8a6',
  Restaurant: '#f97316',
  Fitness: '#10b981',
  Agency: '#3b82f6'
}

export default function TemplatePickerModal({ isOpen, onClose, onSelectTemplate }) {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [hoveredTemplate, setHoveredTemplate] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  if (!isOpen) return null

  const categories = ['All', ...new Set(PRELOADED_TEMPLATES.map(t => t.category))]

  let filtered = selectedCategory === 'All'
    ? PRELOADED_TEMPLATES
    : PRELOADED_TEMPLATES.filter(t => t.category === selectedCategory)

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase()
    filtered = filtered.filter(t =>
      t.name.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q)
    )
  }

  const handleSelect = (template) => {
    const clonedBlocks = JSON.parse(JSON.stringify(template.blocks))
    const reIdBlocks = (blocks) => {
      return blocks.map(block => {
        const newBlock = {
          ...block,
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        }
        if (block.type === 'columns' && block.settings.columns) {
          newBlock.settings = {
            ...newBlock.settings,
            columns: block.settings.columns.map((col, idx) => ({
              ...col,
              id: `col-${idx}`,
              blocks: col.blocks ? col.blocks.map(b => ({
                ...b,
                id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
              })) : []
            }))
          }
        }
        return newBlock
      })
    }

    onSelectTemplate({
      subject: template.subject,
      blocks: reIdBlocks(clonedBlocks)
    })
    onClose()
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '2rem',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          width: '100%',
          maxWidth: '860px',
          height: '85vh',
          maxHeight: '720px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '1px solid #f1f5f9'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>
              Choose a Template
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
              Pick a starting point for your email
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
              color: '#64748b',
              display: 'flex',
              transition: 'all 0.15s'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#334155' }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body: Sidebar + Grid */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Sidebar Categories */}
          <div style={{
            width: '180px',
            flexShrink: 0,
            borderRight: '1px solid #f1f5f9',
            padding: '12px 8px',
            overflowY: 'auto',
            background: '#fafbfc'
          }}>
            {categories.map(cat => {
              const CatIcon = cat === 'All' ? Layers : (CATEGORY_ICONS[cat] || FileText)
              const isActive = selectedCategory === cat
              const catColor = CATEGORY_COLORS[cat] || '#6366f1'
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    background: isActive ? '#f0f0ff' : 'transparent',
                    color: isActive ? '#6366f1' : '#64748b',
                    fontSize: '13px',
                    fontWeight: isActive ? '600' : '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                    marginBottom: '2px'
                  }}
                >
                  <CatIcon size={15} style={{ flexShrink: 0, color: isActive ? '#6366f1' : '#94a3b8' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</span>
                </button>
              )
            })}
          </div>

          {/* Main Content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Search */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '7px 12px'
              }}>
                <Search size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'none',
                    outline: 'none',
                    fontSize: '13px',
                    color: '#334155',
                    width: '100%'
                  }}
                />
              </div>
            </div>

            {/* Template Grid */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              alignContent: 'start'
            }}>
              {/* Blank Template */}
              <div
                onClick={() => {
                  onSelectTemplate({ subject: '', blocks: [] })
                  onClose()
                }}
                onMouseEnter={() => setHoveredTemplate('blank')}
                onMouseLeave={() => setHoveredTemplate(null)}
                style={{
                  border: hoveredTemplate === 'blank' ? '1.5px solid #6366f1' : '1.5px dashed #d1d5db',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '24px 12px',
                  transition: 'all 0.15s',
                  background: hoveredTemplate === 'blank' ? '#f8f7ff' : '#fafafa',
                  minHeight: '150px'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: hoveredTemplate === 'blank' ? '#ede9fe' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s'
                }}>
                  <Plus size={18} style={{ color: hoveredTemplate === 'blank' ? '#6366f1' : '#94a3b8' }} />
                </div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Blank Template</span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Start from scratch</span>
              </div>

              {/* Template Cards */}
              {filtered.map((template) => {
                const CategoryIcon = CATEGORY_ICONS[template.category] || FileText
                const categoryColor = CATEGORY_COLORS[template.category] || '#6366f1'
                const isHovered = hoveredTemplate === template.id

                return (
                  <div
                    key={template.id}
                    onClick={() => handleSelect(template)}
                    onMouseEnter={() => setHoveredTemplate(template.id)}
                    onMouseLeave={() => setHoveredTemplate(null)}
                    style={{
                      border: isHovered ? `1.5px solid ${categoryColor}` : '1.5px solid #e5e7eb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      transform: isHovered ? 'translateY(-1px)' : 'none',
                      boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
                      background: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '200px',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Color accent bar */}
                    <div style={{
                      height: '3px',
                      background: categoryColor,
                      opacity: isHovered ? 1 : 0.4,
                      transition: 'opacity 0.15s'
                    }} />

                    {/* Card content */}
                    <div style={{ padding: '18px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      {/* Icon + Category */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: `${categoryColor}14`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <CategoryIcon size={16} style={{ color: categoryColor }} />
                        </div>
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          color: categoryColor,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          background: `${categoryColor}0D`,
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          {template.category}
                        </span>
                      </div>

                      {/* Name */}
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '4px',
                        lineHeight: '1.3'
                      }}>
                        {template.name}
                      </div>

                      {/* Description */}
                      <div style={{
                        fontSize: '11.5px',
                        color: '#94a3b8',
                        lineHeight: '1.45',
                        flex: 1
                      }}>
                        {template.description}
                      </div>

                      {/* Footer */}
                      <div style={{
                        marginTop: '10px',
                        paddingTop: '8px',
                        borderTop: '1px solid #f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <span style={{ fontSize: '11px', color: '#cbd5e1' }}>
                          {template.blocks.length} blocks
                        </span>
                        <span style={{
                          fontSize: '11px',
                          color: isHovered ? categoryColor : '#cbd5e1',
                          fontWeight: '500',
                          transition: 'color 0.15s'
                        }}>
                          Use template →
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}

              {filtered.length === 0 && (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#94a3b8'
                }}>
                  <Search size={24} style={{ marginBottom: '8px', opacity: 0.4 }} />
                  <div style={{ fontSize: '13px' }}>No templates found</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
